import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, ArrowRight, Zap, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import { getPricing, createCheckout } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/lib/seo";
import { toast } from "sonner";

const KIND_LABEL = {
  one_time: "One-time",
  monthly_pass: "/month",
};

export default function Pricing() {
  usePageMeta({
    title: "Pricing — MonetizeGPS | Blueprints from $29",
    description: "1 free preview, then $29 for a single Blueprint or monthly plans from $49 for 10 Blueprints. Fair, honest pricing for monetization analysis.",
    path: "/pricing",
  });
  const { user, loading: authLoading } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    getPricing()
      .then(setPackages)
      .catch(() => toast.error("Couldn't load pricing"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (params.get("cancelled") === "1") {
      toast.info("Payment cancelled. No charge.");
    }
  }, [params]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/pricing";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const buy = async (pkgId) => {
    if (!user) {
      toast.info("Sign in first — we need to remember your credits.");
      setTimeout(handleLogin, 800);
      return;
    }
    setCheckoutLoading(pkgId);
    try {
      const { url } = await createCheckout(pkgId);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout.");
      setCheckoutLoading(null);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 font-mono-plex text-sm" data-testid="pricing-loading">
          Loading pricing...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain" data-testid="pricing-page">
      <Header />

      {/* Hero */}
      <section className="border-b-2 border-[#111]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono-plex text-[11px] uppercase tracking-[0.3em] text-[#555]">
              § Pricing · Fair, honest, no BS
            </span>
            <span className="flex-1 h-px bg-[#111]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <h1 className="md:col-span-7 font-serif-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.88]">
              Pay for <em className="text-[#FF331F] not-italic">lookups</em>.<br />Skip the fluff.
            </h1>
            <p className="md:col-span-5 text-base md:text-lg text-[#333] leading-relaxed">
              1 free preview lookup, no signup — you&apos;ll see the #1 play in full. Sign up (free) to unlock the rest, or pick a plan below.
            </p>
          </div>
        </div>
      </section>

      {/* Packages grid */}
      <section className="border-b-2 border-[#111]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-[#111] bg-white" data-testid="pricing-grid">
            {packages.map((pkg, i) => (
              <PricingCard
                key={pkg.id}
                pkg={pkg}
                index={i}
                total={packages.length}
                loading={checkoutLoading === pkg.id}
                anyLoading={!!checkoutLoading}
                onBuy={() => buy(pkg.id)}
              />
            ))}
          </div>

          <p className="mt-6 font-mono-plex text-[10px] uppercase tracking-widest text-[#555] text-center">
            * All prices USD · Payments processed by Stripe · Monthly plans auto-renew · Cancel anytime
          </p>
        </div>
      </section>

      {/* Monitoring — included with monthly plans */}
      <section className="border-b-2 border-[#111]" data-testid="pricing-monitoring-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="flex items-center gap-3 mb-6">
            <span className="font-mono-plex text-[11px] uppercase tracking-[0.3em] text-[#555]">
              § Included with every monthly plan
            </span>
            <span className="flex-1 h-px bg-[#111]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-12">
            <h2 className="md:col-span-7 font-serif-display text-4xl md:text-5xl font-black tracking-tighter leading-[0.92]">
              Your sites. Their sites.<br /><em className="text-[#1E40AF] not-italic">Watched monthly.</em>
            </h2>
            <p className="md:col-span-5 text-sm md:text-base text-[#333] leading-relaxed">
              A Blueprint is a snapshot. Monitoring is the movie. Track your own launches, your
              clients&apos; sites — and your competitors&apos;. You&apos;ll know the moment anyone&apos;s
              monetization moves.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 border-2 border-[#111] bg-white divide-y-2 lg:divide-y-0 lg:divide-x-2 divide-[#111]">
            {[
              ["01", "Scan it. It's watched.", "Every URL you analyze joins your Watchlist automatically — your site, a client's, or a competitor's. No buttons, no setup, nothing to manage."],
              ["02", "Full rescan, every month.", "Each watched site gets a complete fresh analysis monthly — new Blueprint, new scores, diff against the last one. Included free, never uses your lookups."],
              ["03", "Alerts that earn attention.", "A score jumps 10+ points, a pricing page appears, a new play breaks 65 — you get what changed, why it matters, and what to do next. In-app and by email."],
              ["04", "Silence when nothing moves.", "No meaningful change, no notification. We don't spam you to look busy."],
            ].map(([num, title, body]) => (
              <div key={num} className="p-6 md:p-7">
                <div className="score-display text-3xl text-[#1E40AF]">{num}</div>
                <h3 className="mt-3 font-serif-display text-xl font-bold leading-tight">{title}</h3>
                <p className="mt-2 text-sm text-[#333] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>

          <p className="mt-6 font-mono-plex text-[10px] uppercase tracking-widest text-[#555] text-center" data-testid="monitoring-limits-line">
            Starter watches 5 sites · Agency 20 · Founder 75 — each with 1 free manual rescan per month on top of your lookups
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b-2 border-[#111]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-3">§ Pricing FAQ</div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold tracking-tight leading-none">
              Common questions.
            </h2>
          </div>
          <div className="md:col-span-8 border-2 border-[#111] bg-white divide-y-2 divide-[#111]">
            {FAQ.map((f, i) => (
              <details key={i} className="group p-5" data-testid={`pricing-faq-${i}`}>
                <summary className="cursor-pointer font-serif-display text-lg font-bold list-none flex justify-between items-start gap-4">
                  <span>{f.q}</span>
                  <span className="font-mono-plex text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-[#333] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function PricingCard({ pkg, index, total, loading, anyLoading, onBuy }) {
  const highlighted = pkg.highlighted || pkg.id === "value";
  const isMonthly = pkg.kind === "monthly_pass";
  const rightBorder = index < total - 1 ? "lg:border-r-2" : "";
  const bottomBorder = index < total - 1 ? "border-b-2 lg:border-b-0" : "";
  return (
    <div
      className={`p-6 md:p-7 flex flex-col border-[#111] ${rightBorder} ${bottomBorder} ${highlighted ? "bg-[#111] text-[#F4F4F0]" : "bg-white text-[#111]"}`}
      data-testid={`pricing-card-${pkg.id}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`font-mono-plex text-[10px] uppercase tracking-[0.25em] ${highlighted ? "text-[#F4F4F0]/70" : "text-[#555]"}`}>
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </div>
        {highlighted && (
          <span className="font-mono-plex text-[9px] uppercase tracking-[0.25em] bg-[#FF331F] text-white px-2 py-0.5">
            Most popular
          </span>
        )}
        {isMonthly && !highlighted && (
          <span className="font-mono-plex text-[9px] uppercase tracking-[0.25em] bg-[#FF331F] text-white px-2 py-0.5 flex items-center gap-1">
            <Zap size={10} /> Recurring
          </span>
        )}
      </div>

      <h3 className={`font-serif-display text-2xl font-bold leading-tight ${highlighted ? "text-[#F4F4F0]" : ""}`}>{pkg.name}</h3>

      <div className="mt-4 flex items-baseline gap-1">
        <span className={`score-display text-5xl leading-none ${highlighted ? "text-[#F4F4F0]" : "text-[#111]"}`} data-testid={`price-${pkg.id}`}>
          ${Math.floor(pkg.price)}
        </span>
        <span className={`font-mono-plex text-xs ${highlighted ? "text-[#F4F4F0]/70" : "text-[#555]"}`}>
          {isMonthly ? "/mo" : ""}
        </span>
      </div>
      <div className={`mt-1 font-mono-plex text-[10px] uppercase tracking-[0.25em] ${highlighted ? "text-[#F4F4F0]/70" : "text-[#555]"}`}>
        {KIND_LABEL[pkg.kind] || pkg.kind}
      </div>

      <p className={`mt-4 text-sm leading-relaxed ${highlighted ? "text-[#F4F4F0]/90" : "text-[#333]"}`}>
        {pkg.description}
      </p>

      <ul className="mt-5 space-y-2 flex-1">
        {pkg.features?.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <Check size={14} className={`mt-0.5 shrink-0 ${highlighted ? "text-[#FF331F]" : "text-[#0A8754]"}`} />
            <span className={highlighted ? "text-[#F4F4F0]" : "text-[#222]"}>{f}</span>
          </li>
        ))}
      </ul>

      <button
        onClick={onBuy}
        disabled={anyLoading}
        className={`mt-6 inline-flex items-center justify-center gap-2 px-4 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
          highlighted
            ? "bg-[#FF331F] text-white hover:bg-white hover:text-[#111]"
            : "bg-[#111] text-[#F4F4F0] hover:bg-[#FF331F]"
        }`}
        data-testid={`buy-${pkg.id}-button`}
      >
        {loading ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Opening...
          </>
        ) : (
          <>
            Get {pkg.name} <ArrowRight size={14} />
          </>
        )}
      </button>
    </div>
  );
}

const FAQ = [
  {
    q: "What do I get for free?",
    a: "Your first lookup is a free preview — no signup required. You'll see the #1 monetization play in full detail with its score, evidence, and next steps. Plays #2–5, the executive summary, and API recommendations are unlocked when you sign up (still free — no card needed) or start a plan.",
  },
  {
    q: "How long does an analysis take?",
    a: "Most sites take 20–45 seconds. Larger sites with lots of content can take up to a minute. You'll see a live terminal showing what MonetizeGPS is doing at each step.",
  },
  {
    q: "What counts as a lookup?",
    a: "Analyzing a NEW URL uses 1 lookup — whether it's from the extension or the website. Diff view, sharing, and email delivery are always free. Monitoring is free on top: automatic monthly rescans and each watched site's included manual rescan never touch your lookups.",
  },
  {
    q: "What is the Watchlist (monthly monitoring)?",
    a: "On any monthly plan, every URL you scan is automatically watched — no setup. Each watched site gets one full automatic rescan every month with a diff against the previous Blueprint. If something meaningful changed, you get an alert in the app and by email; if not, it updates quietly. Starter watches up to 5 sites, Agency 20, Founder 75.",
  },
  {
    q: "Do monitoring rescans use my lookups?",
    a: "No. Automatic monthly rescans are included free for every watched site, and each watched site also gets 1 free manual 'Rescan Now' per billing month. Your lookups stay reserved for scanning new URLs. The free manual rescan doesn't roll over — it resets fresh each billing cycle.",
  },
  {
    q: "What alerts will I get?",
    a: "Only meaningful monetization changes: a play's score moving 10+ points, a new play scoring 65+, a pricing page or checkout flow appearing or disappearing, new email-capture or affiliate signals, a monetization route vanishing. Tiny copy edits, image swaps, and design tweaks never trigger alerts — no noise, ever.",
  },
  {
    q: "What happens when my Watchlist is full?",
    a: "New scans still complete normally and save to your Blueprint history — they just don't start monitoring. You can pause or remove a watched site to free a space, or upgrade for a bigger Watchlist.",
  },
  {
    q: "What is Rescan?",
    a: "Rescan re-analyzes any URL you've already scanned — pulling the latest data from the site. Websites change constantly (new pricing pages, added checkout flows, dropped features), and Rescan catches it all. Watched sites get 1 free manual rescan per month; beyond that, a rescan uses 1 lookup.",
  },
  {
    q: "What is Diff View?",
    a: "Diff View is where MonetizeGPS earns its price. When you rescan a URL, MonetizeGPS compares the old and new scans side-by-side and shows you exactly what changed — new monetization opportunities, plays that dropped in score, on-site signal changes, and specific next actions. Think stock ticker for monetization strategy. Diff View is included free with every rescan.",
  },
  {
    q: "What happens to unused lookups?",
    a: "Monthly plan lookups refill to the full amount automatically each billing cycle (industry standard, same as Grammarly and ChatGPT Plus). The $29 Single Blueprint never expires.",
  },
  {
    q: "Can I cancel the monthly anytime?",
    a: "Yes. Monthly plans auto-renew, and you can cancel anytime in one click — click your avatar, open your Account, and hit Manage Subscription (Stripe's secure portal handles it). You keep access and any remaining lookups until the end of your paid period, and you're never charged again after cancelling.",
  },
  {
    q: "Which plan is right for me?",
    a: "Testing the waters? Grab a Single Blueprint ($29). Regular use? Starter ($49/mo). Working multiple deals? Agency ($99/mo). Running at volume? Founder ($249/mo).",
  },
  {
    q: "Is my payment secure?",
    a: "Yes. All payments are processed by Stripe. MonetizeGPS never sees or stores your card details.",
  },
];
